import React, { useEffect, useRef } from 'react';
import {
    CLEAR_COLOR_RGB,
    CSS_PX_PER_PT,
    GRID_OVERFILL_CELLS,
    GRID_SPACING_CSS_PT,
    NOISE_RESOLUTION,
    NOISE_SPEED,
    OFFSET_MAGNITUDE_CSS_PX,
    PARTICLE_COLOR_RGB,
    PARTICLE_POINT_SIZE_CSS_PT,
} from './particleBackgroundConfig';
import { gridDimensionsFromSpacingCssPt } from './particleGridLayout';
import { PARTICLE_FRAGMENT_SHADER, PARTICLE_VERTEX_SHADER } from './particleBackgroundShaders';
import styles from './ParticleBackgroundCanvas.module.css';

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.warn('[ParticleBackground] vertex/fragment compile:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

function createProgram(
    gl: WebGL2RenderingContext,
    vertexSource: string,
    fragmentSource: string,
): WebGLProgram | null {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vs || !fs) return null;
    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.warn('[ParticleBackground] program link:', gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
    }
    return program;
}

/**
 * Full-viewport WebGL behind content: `POINTS` grid with 3D Perlin-driven motion (see config).
 * Particle count follows {@link GRID_SPACING_CSS_PT}.
 */
const ParticleBackgroundCanvas: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const gl = canvas.getContext('webgl2', {
            alpha: false,
            antialias: false,
            depth: false,
            stencil: false,
            premultipliedAlpha: false,
        });
        if (!gl) {
            console.warn('[ParticleBackground] WebGL2 not available');
            return;
        }

        const program = createProgram(gl, PARTICLE_VERTEX_SHADER, PARTICLE_FRAGMENT_SHADER);
        if (!program) return;

        const uResolution = gl.getUniformLocation(program, 'u_resolution');
        const uCols = gl.getUniformLocation(program, 'u_cols');
        const uParticleCount = gl.getUniformLocation(program, 'u_particleCount');
        const uCellSpacingPx = gl.getUniformLocation(program, 'u_cellSpacingPx');
        const uOverfillCells = gl.getUniformLocation(program, 'u_overfillCells');
        const uPointSize = gl.getUniformLocation(program, 'u_pointSize');
        const uParticleColor = gl.getUniformLocation(program, 'u_particleColor');
        const uTime = gl.getUniformLocation(program, 'u_time');
        const uNoiseSpeed = gl.getUniformLocation(program, 'u_noiseSpeed');
        const uOffsetMagnitudePx = gl.getUniformLocation(program, 'u_offsetMagnitudePx');
        const uNoiseResolution = gl.getUniformLocation(program, 'u_noiseResolution');

        const clearR = CLEAR_COLOR_RGB[0] / 255;
        const clearG = CLEAR_COLOR_RGB[1] / 255;
        const clearB = CLEAR_COLOR_RGB[2] / 255;
        const pr = PARTICLE_COLOR_RGB[0] / 255;
        const pg = PARTICLE_COLOR_RGB[1] / 255;
        const pb = PARTICLE_COLOR_RGB[2] / 255;

        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.STENCIL_TEST);
        gl.disable(gl.CULL_FACE);

        const vao = gl.createVertexArray();
        if (!vao) return;
        gl.bindVertexArray(vao);

        const particleIndexLoc = gl.getAttribLocation(program, 'a_particleIndex');
        if (particleIndexLoc < 0) {
            console.warn('[ParticleBackground] missing a_particleIndex attribute');
            gl.deleteVertexArray(vao);
            gl.deleteProgram(program);
            return;
        }
        const indexBuffer = gl.createBuffer();
        if (!indexBuffer) {
            gl.deleteVertexArray(vao);
            gl.deleteProgram(program);
            return;
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, indexBuffer);
        gl.enableVertexAttribArray(particleIndexLoc);
        gl.vertexAttribPointer(particleIndexLoc, 1, gl.FLOAT, false, 0, 0);

        gl.useProgram(program);
        gl.uniform3f(uParticleColor, pr, pg, pb);
        gl.uniform1f(uNoiseSpeed, NOISE_SPEED);
        gl.uniform1f(uNoiseResolution, NOISE_RESOLUTION);

        let particleCount = 0;
        let bufferedCount = -1;

        const ensureIndexBuffer = (count: number) => {
            if (count === bufferedCount) return;
            bufferedCount = count;
            const particleIndices = new Float32Array(count);
            for (let i = 0; i < count; i += 1) {
                particleIndices[i] = i;
            }
            gl.bindBuffer(gl.ARRAY_BUFFER, indexBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, particleIndices, gl.DYNAMIC_DRAW);
            gl.vertexAttribPointer(particleIndexLoc, 1, gl.FLOAT, false, 0, 0);
        };

        const paint = () => {
            gl.clearColor(clearR, clearG, clearB, 1);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.useProgram(program);
            gl.bindVertexArray(vao);
            if (particleCount > 0) {
                gl.drawArrays(gl.POINTS, 0, particleCount);
            }
        };

        const t0 = performance.now();
        let raf = 0;

        const frame = () => {
            gl.useProgram(program);
            gl.uniform1f(uTime, (performance.now() - t0) / 1000);
            paint();
            raf = requestAnimationFrame(frame);
        };

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const w = Math.floor(window.innerWidth * dpr);
            const h = Math.floor(window.innerHeight * dpr);
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w;
                canvas.height = h;
            }
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.useProgram(program);
            gl.uniform2f(uResolution, canvas.width, canvas.height);

            const { cols, particleCount: n } = gridDimensionsFromSpacingCssPt(
                canvas.width,
                canvas.height,
                dpr,
                GRID_SPACING_CSS_PT,
                GRID_OVERFILL_CELLS,
            );
            particleCount = n;
            gl.uniform1f(uCols, cols);
            gl.uniform1f(uParticleCount, particleCount);
            gl.uniform1f(uCellSpacingPx, GRID_SPACING_CSS_PT * CSS_PX_PER_PT * dpr);
            gl.uniform1f(uOverfillCells, GRID_OVERFILL_CELLS);

            ensureIndexBuffer(particleCount);

            const pointSizeFb = PARTICLE_POINT_SIZE_CSS_PT * CSS_PX_PER_PT * dpr;
            const sizeRange = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as Float32Array;
            const clampedPointSize = Math.min(Math.max(pointSizeFb, sizeRange[0]), sizeRange[1]);
            gl.uniform1f(uPointSize, clampedPointSize);
            gl.uniform1f(uOffsetMagnitudePx, OFFSET_MAGNITUDE_CSS_PX * dpr);
        };

        resize();
        window.addEventListener('resize', resize);
        raf = requestAnimationFrame(frame);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            gl.deleteBuffer(indexBuffer);
            gl.deleteVertexArray(vao);
            gl.deleteProgram(program);
        };
    }, []);

    return <canvas ref={canvasRef} className={styles.root} aria-hidden />;
};

export default ParticleBackgroundCanvas;
